import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { apiClient } from '@/lib/api-client';
import type { ApiClientError, ApiSuccess } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/efs/PageHeader';
import { BatchStatusBadge, type BatchStatus } from '@/components/efs/BatchStatusBadge';
import { KgValue } from '@/components/efs/KgValue';
import { formatBatchFacility, formatBatchMonth } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { useCloseBatch, useRequestClose } from '@/features/mass-balance/useMassBalance';
import CloseDelayPanel from '@/features/governance/close-delay';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';

interface MonthlyBatchDetailDto {
  monthly_batch_id: string;
  facility_id: string;
  facility_name: string;
  facility_role: string;
  company_display_name: string;
  company_party_type: string;
  company_short_code: string | null;
  year_month: string;
  // DISPLAY-ONLY batch code, e.g. EFS_GIDK_202604 (or EFS_202604 when the
  // facility company has no short code). See BE monthly_batches model.
  display_code: string;
  status: BatchStatus;
  opening_wip_kg: string | null;
  opening_material_inventory_kg: string | null;
  opening_balance_confirmed_by: string | null;
  opening_balance_confirmed_at: string | null;
  closed_at: string | null;
  closed_by: string | null;
  package_generated_at: string | null;
  // §18 close-delay context (migration 0048); may be absent on older batches.
  case_code: string | null;
  close_deadline: string | null;
}

const NS = 'batches';

const fetchBatch = async (id: string): Promise<MonthlyBatchDetailDto> => {
  const res = await apiClient.get<ApiSuccess<MonthlyBatchDetailDto>>(`/monthly-batches/${id}`);
  return res.data.data;
};

const SUB_LINKS = [
  { suffix: 'imports', i18nKey: 'efs.monthly-batches.detail.links.imports' },
  { suffix: 'inbound', i18nKey: 'efs.monthly-batches.detail.links.inbound' },
  { suffix: 'sorting', i18nKey: 'efs.monthly-batches.detail.links.sorting' },
  { suffix: 'dismantling', i18nKey: 'efs.monthly-batches.detail.links.dismantling' },
  { suffix: 'inventory', i18nKey: 'efs.monthly-batches.detail.links.inventory' },
  { suffix: 'outbounds', i18nKey: 'efs.monthly-batches.detail.links.outbounds' },
  { suffix: 'evidence', i18nKey: 'efs.monthly-batches.detail.links.evidence' },
  { suffix: 'exceptions', i18nKey: 'efs.monthly-batches.detail.links.exceptions' },
  { suffix: 'approvals', i18nKey: 'efs.monthly-batches.detail.links.approvals' },
  { suffix: 'mass-balance', i18nKey: 'efs.monthly-batches.detail.links.mass_balance' },
  { suffix: 'audit-package', i18nKey: 'efs.monthly-batches.detail.links.audit_package' },
] as const;

export const BatchDetailPage = () => {
  const { t } = useTranslation([NS, 'common']);
  const { batchId } = useParams<{ batchId: string }>();
  const user = useCurrentUser();
  const requestClose = useRequestClose();
  const closeBatch = useCloseBatch();

  const query = useQuery<MonthlyBatchDetailDto, ApiClientError>({
    queryKey: ['monthly-batches', 'detail', batchId],
    queryFn: () => fetchBatch(batchId!),
    enabled: !!batchId,
  });

  const batch = query.data;
  const status = batch?.status;

  const canManage = hasPermission(user, 'batch.manage');
  const canClose = hasPermission(user, 'monthly_close.approve');

  // Which actions to surface depending on lifecycle status.
  const showOpeningBalance = status === 'draft' || status === 'open';
  const showRunMassBalance = status === 'open' || status === 'reopened';
  const showRequestClose = (status === 'open' || status === 'reopened') && canManage;
  const showCloseBatch = status === 'pending_approval' && canClose;
  const showGeneratePackage = status === 'closed';

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          batch ? (
            <span className="flex items-baseline gap-2">
              {`${t('efs.monthly-batches.detail.title')} — ${formatBatchMonth(batch.year_month)} · ${formatBatchFacility(batch)}`}
              <span className="font-mono text-xs font-normal text-efs-text-muted">
                {batch.display_code}
              </span>
            </span>
          ) : (
            t('efs.monthly-batches.detail.title')
          )
        }
        subtitle={t('efs.monthly-batches.detail.subtitle')}
        actions={batch ? <BatchStatusBadge status={batch.status} /> : null}
      />

      {query.isLoading ? (
        <p className="text-sm text-efs-text-muted">
          {t('efs.common.messages.loading', { ns: 'common' })}
        </p>
      ) : query.isError ? (
        <div className="efs-panel space-y-3 p-6">
          <LocalizedApiError error={query.error} showTechnicalDetails={false} />
          <Button type="button" variant="outline" onClick={() => void query.refetch()}>
            {t('efs.common.actions.retry', { ns: 'common', defaultValue: '再試行' })}
          </Button>
        </div>
      ) : !batch ? (
        <p className="text-sm text-efs-text-muted">
          {t('efs.monthly-batches.detail.messages.not_found')}
        </p>
      ) : (
        <>
          <section className="efs-panel space-y-3 p-6">
            <h2 className="text-sm font-semibold text-efs-text-primary">
              {t('efs.monthly-batches.detail.sections.overview')}
            </h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.facility', {
                    defaultValue: '作業據點',
                  })}
                </dt>
                <dd className="text-sm">{formatBatchFacility(batch)}</dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.facility_role', {
                    defaultValue: '據點角色',
                  })}
                </dt>
                <dd className="text-sm">{batch.facility_role}</dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.year_month')}
                </dt>
                <dd className="font-mono text-sm">{formatBatchMonth(batch.year_month)}</dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.display_code')}
                </dt>
                <dd className="font-mono text-sm text-efs-text-muted">{batch.display_code}</dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.status')}
                </dt>
                <dd className="text-sm">
                  <BatchStatusBadge status={batch.status} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.opening_wip_kg')}
                </dt>
                <dd className="text-sm">
                  {batch.opening_wip_kg !== null ? <KgValue value={batch.opening_wip_kg} /> : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.opening_material_inventory_kg')}
                </dt>
                <dd className="text-sm">
                  {batch.opening_material_inventory_kg !== null ? (
                    <KgValue value={batch.opening_material_inventory_kg} />
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.closed_at')}
                </dt>
                <dd className="text-sm">
                  {batch.closed_at ? new Date(batch.closed_at).toLocaleString() : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.closed_by')}
                </dt>
                <dd className="text-sm">{batch.closed_by ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.package_generated_at')}
                </dt>
                <dd className="text-sm">
                  {batch.package_generated_at
                    ? new Date(batch.package_generated_at).toLocaleString()
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.opening_balance_confirmed_by')}
                </dt>
                <dd className="text-sm">{batch.opening_balance_confirmed_by ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-efs-text-muted">
                  {t('efs.monthly-batches.detail.fields.opening_balance_confirmed_at')}
                </dt>
                <dd className="text-sm">
                  {batch.opening_balance_confirmed_at
                    ? new Date(batch.opening_balance_confirmed_at).toLocaleString()
                    : '—'}
                </dd>
              </div>
            </dl>
          </section>

          <section className="efs-panel space-y-3 p-6">
            <h2 className="text-sm font-semibold text-efs-text-primary">
              {t('efs.monthly-batches.detail.sections.actions')}
            </h2>
            <div className="flex flex-wrap gap-2">
              {showOpeningBalance ? (
                <Button asChild variant="outline">
                  <Link to={`/batches/${batch.monthly_batch_id}/opening-balance`}>
                    {t('efs.monthly-batches.detail.actions.open_opening_balance')}
                  </Link>
                </Button>
              ) : null}
              {showRunMassBalance ? (
                <Button asChild variant="outline">
                  <Link to={`/batches/${batch.monthly_batch_id}/mass-balance`}>
                    {t('efs.monthly-batches.detail.actions.run_mass_balance')}
                  </Link>
                </Button>
              ) : null}
              {showRequestClose ? (
                <Button
                  variant="outline"
                  onClick={() => requestClose.mutate(batch.monthly_batch_id)}
                  disabled={requestClose.isPending}
                >
                  {requestClose.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : null}
                  {t('efs.monthly-batches.detail.actions.request_close')}
                </Button>
              ) : null}
              {showCloseBatch ? (
                <Button
                  variant="default"
                  onClick={() => closeBatch.mutate(batch.monthly_batch_id)}
                  disabled={closeBatch.isPending}
                >
                  {closeBatch.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  {t('efs.monthly-batches.detail.actions.close_batch')}
                </Button>
              ) : null}
              {showGeneratePackage ? (
                <Button asChild>
                  <Link to={`/batches/${batch.monthly_batch_id}/audit-package`}>
                    {t('efs.monthly-batches.detail.actions.generate_package')}
                  </Link>
                </Button>
              ) : null}
            </div>
          </section>

          <section className="efs-panel space-y-3 p-6">
            <h2 className="text-sm font-semibold text-efs-text-primary">
              {t('efs.monthly-batches.detail.sections.navigation')}
            </h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              {SUB_LINKS.map((link) => (
                <Button key={link.suffix} asChild variant="ghost" className="justify-start">
                  <Link to={`/batches/${batch.monthly_batch_id}/${link.suffix}`}>
                    {t(link.i18nKey)}
                  </Link>
                </Button>
              ))}
            </div>
          </section>

          <CloseDelayPanel batch={batch} />
        </>
      )}
    </div>
  );
};

export default BatchDetailPage;
